/**
 * De los DTOs del backend a los modelos que dibujan las pantallas.
 *
 * Por qué existe esta capa en vez de renderizar el DTO directo: el backend
 * manda listas PLANAS y desnormalizadas (`company_name`, `company_color_hue`,
 * `stage_label`) y detalles con ids pelados (`internal_owner_id`,
 * `client_contact_id`). Las dos formas describen lo mismo, pero una pantalla no
 * puede tener dos maneras de preguntar "¿de qué empresa es esto?".
 *
 * **Lo que el backend no manda, acá no se inventa.** Cada hueco está marcado
 * con `FALTA:` y sale como `null`, para que la pantalla decida qué hacer con la
 * ausencia. Un default plausible (un `health: 'green'` porque queda lindo) es
 * peor que un vacío: se ve igual que un dato real.
 */

import type {
  ClientMeRead,
  CompanyRead,
  ComponentLinkRead,
  ComponentRead,
  ContactRead,
  DocumentListItem,
  DocumentRead,
  ProjectListItem,
  ProjectRead,
  RepositoryRead,
  StageRead,
  StageSetRead,
  TaskListItem,
  TaskRead,
  TicketItem,
  UserRead,
  MeRead,
  UserListItem,
  AuditEntryRead,
  ArchitectureTemplateRead,
} from '@/shared/api/contracts'
import type {
  ComponentLink,
  ProjectComponent,
  ProjectRepository,
  ArchitectureTemplate,
} from '@/features/architecture/types'
import type { CompanyAccess } from '@/features/accesses/types'
import type { AccessRead } from '@/shared/api/contracts'
import type { DocumentDetail, DocumentSummary } from '@/features/docs/types'
import { resolveLocale } from '@/shared/i18n/locale'
import { diffDeAuditoria } from '@/features/projects/activity-diff'
import { resolveTimeZone } from '@/shared/lib/due-date'
import type { StageSet, StageSetting, SettingsUser } from '@/features/settings/types'
import type {
  ActivityEntry,
  CompanyContact,
  CompanySummary,
  Me,
  ProjectSummary,
  Stage,
  StageCategory,
  SupportTicket,
  Task,
  UserSummary,
  Uuid,
} from '@/shared/types/domain'

/* --------------------------------------------------------------- índices - */

/**
 * `id -> usuario`. Los listados del backend traen `assignee_id` y
 * `internal_owner_id`, nunca el objeto: el nombre sale de `GET /users`, que ya
 * está cacheado 10 minutos porque lo usan todos los selectores.
 */
export type UserIndex = ReadonlyMap<Uuid, UserSummary>

export function buildUserIndex(users: readonly UserSummary[]): UserIndex {
  return new Map(users.map((user) => [user.id, user]))
}

export const EMPTY_USER_INDEX: UserIndex = new Map()

function lookupUser(index: UserIndex, id: Uuid | null): UserSummary | null {
  if (!id) return null
  return index.get(id) ?? null
}

/* ------------------------------------------------------------- identidad - */

export function toUserSummary(dto: UserRead): UserSummary {
  return { id: dto.id, display_name: dto.display_name, email: dto.email }
}

export function toMe(dto: MeRead): Me {
  return {
    id: dto.id,
    email: dto.email,
    display_name: dto.display_name,
    role: dto.role,
    timezone: dto.timezone,
    locale: resolveLocale(dto.locale),
    digest_hour: dto.digest_hour,
    client_company_id: dto.client_company_id,
    company_ids: dto.company_ids,
    is_admin: dto.is_admin,
  }
}

/**
 * `GET /portal/me` -> `Me`, para el contacto de cliente.
 *
 * El rol **no viene en la respuesta y tampoco se adivina**: que este endpoint
 * conteste 200 ya prueba que quien pregunta es del lado cliente (el portal
 * resuelve su empresa desde su fila en `users`, nunca de un parámetro), así que
 * `client` es lo que el servidor está afirmando, no una suposición del front.
 *
 * Lo que el schema del portal no expone queda en su valor más restrictivo:
 * `is_admin: false` y una sola empresa. `timezone` y `digest_hour` son ajustes
 * de notificación que el portal no muestra ni deja tocar; se completan con el
 * default del navegador para no inventar una zona ajena.
 */
export function toMeFromPortal(dto: ClientMeRead): Me {
  return {
    id: dto.id,
    email: dto.email,
    display_name: dto.display_name,
    role: 'client',
    timezone: resolveTimeZone(),
    // `locale` puede no venir (ver `ClientMeRead`): `resolveLocale` baja hasta
    // `es` sin tratarlo como un error.
    locale: resolveLocale(dto.locale),
    digest_hour: 9,
    client_company_id: dto.company_id,
    company_ids: [dto.company_id],
    is_admin: false,
  }
}

/**
 * `GET /users` devuelve `UserListItem`, que **sí** trae `company_ids`.
 *
 * `null` queda reservado para las respuestas que no las informan (el alta y el
 * patch devuelven `UserRead` pelado): ahí la pantalla dice "no informado" en vez
 * de mostrar una lista vacía, que se leería como "no tiene ninguna empresa".
 */
export function toSettingsUser(dto: UserRead | UserListItem): SettingsUser {
  return {
    id: dto.id,
    email: dto.email,
    display_name: dto.display_name,
    role: dto.role,
    is_active: dto.is_active,
    client_company_id: dto.client_company_id,
    company_ids: 'company_ids' in dto ? dto.company_ids : null,
    locale: dto.locale,
    last_login_at: dto.last_login_at,
  }
}

/* --------------------------------------------------------------- empresas */

export function toCompanySummary(dto: CompanyRead): CompanySummary {
  return {
    id: dto.id,
    slug: dto.slug,
    name: dto.name,
    color_hue: dto.color_hue,
    status: dto.status as CompanySummary['status'],
  }
}

/** La empresa tal como viene embebida en una fila de proyecto o de tarea. */
export function companyFromRow(row: {
  company_id: Uuid
  company_name: string
  company_color_hue: number
}): CompanySummary {
  return {
    id: row.company_id,
    // FALTA: el `slug` no viaja en las filas desnormalizadas. Solo lo usa la
    // navegación, que en estas pantallas va por id.
    slug: '',
    name: row.company_name,
    color_hue: row.company_color_hue,
    status: 'active',
  }
}

export function toCompanyContact(dto: ContactRead): CompanyContact {
  return {
    id: dto.id,
    company_id: dto.company_id,
    name: dto.name,
    email: dto.email,
    role_title: dto.role_title,
    is_primary: dto.is_primary,
    locale: resolveLocale(dto.locale),
    // El backend no dice "tiene login": dice con QUÉ usuario está atado.
    has_login: dto.user_id !== null,
    notes_internal: dto.notes_internal,
  }
}

/* ------------------------------------------------------------------ fases */

export function toStage(dto: StageRead): Stage {
  return {
    id: dto.id,
    name: dto.label,
    category: dto.category,
    position: dto.position,
    color: dto.color_token,
  }
}

export function toStageSetting(dto: StageRead): StageSetting {
  return {
    ...toStage(dto),
    stage_set_id: dto.stage_set_id,
    key: dto.key,
    is_default_entry: dto.is_default_entry,
    // El backend no tiene `is_terminal`: lo que cierra el trabajo es
    // `category === 'done'`, que además es lo que leen las notificaciones.
    is_terminal: dto.category === 'done',
    // Cuántos ítems vivos hay adentro. Es lo que le permite al diálogo de
    // borrado pedir destino sólo cuando la columna tiene algo.
    items_count: dto.items_count,
  }
}

export function toStageSet(dto: StageSetRead): StageSet {
  return {
    id: dto.id,
    company_id: dto.company_id,
    kind: dto.kind,
    name: dto.name,
    is_system: dto.is_system,
    stages: [...dto.stages].sort((a, b) => a.position - b.position).map(toStageSetting),
  }
}

/** Todas las fases de todos los sets de un tipo, sin repetir por `key`. */
export function stagesFromSets(sets: readonly StageSetRead[]): Stage[] {
  const seen = new Map<string, Stage>()
  for (const set of sets) {
    for (const stage of [...set.stages].sort((a, b) => a.position - b.position)) {
      if (!seen.has(stage.key)) seen.set(stage.key, toStage(stage))
    }
  }
  return [...seen.values()]
}

/**
 * El tablero agrupa por `key` porque cada empresa puede tener su propio set, y
 * `BoardColumn.stage_id` viene `null`. Para mover un proyecto hace falta el
 * `stage_id` **del set de ese proyecto**, que se resuelve por `key`.
 */
export function resolveStageIdForKey(
  sets: readonly StageSetRead[],
  stageSetId: Uuid | null,
  key: string,
): Uuid | null {
  const set = sets.find((candidate) => candidate.id === stageSetId) ?? sets[0]
  return set?.stages.find((stage) => stage.key === key)?.id ?? null
}

/**
 * La columna destino **traducida al juego de columnas del proyecto que se
 * movió**.
 *
 * Es el arreglo de un bug que rompía el tablero entero: las columnas se dibujan
 * con `stagesFromSets()`, que aplana todos los sets de-duplicando por `key` y
 * se queda con el primero —el del **sistema**—, mientras que cada proyecto vive
 * en el set clonado de SU empresa. Así, soltar una tarjeta mandaba el
 * `stage_id` del set del sistema y el backend contestaba 409 "La fase pedida no
 * pertenece al juego de columnas del proyecto": cambiar de fase no funcionaba
 * para ningún proyecto, y el usuario veía la tarjeta volver sola.
 *
 * Se traduce por `key`, que es el slug estable y compartido entre el set del
 * sistema y sus clones; el nombre visible sí puede diferir por empresa.
 *
 * `null` = no se pudo traducir (el proyecto está en un set que no llegó, o la
 * columna no existe en él). Quien llama no debe mandar `stage_id` inventado:
 * mejor mover sólo el orden que mover a una fase que no es.
 */
export function resolveStageInSameSet(
  sets: readonly StageSetRead[],
  projectStageId: Uuid,
  targetStageId: Uuid,
): Stage | null {
  const has = (set: StageSetRead, id: Uuid) => set.stages.some((stage) => stage.id === id)

  const targetSet = sets.find((set) => has(set, targetStageId))
  const key = targetSet?.stages.find((stage) => stage.id === targetStageId)?.key
  if (!key) return null

  const projectSet = sets.find((set) => has(set, projectStageId))
  if (!projectSet) return null

  const stage = projectSet.stages.find((candidate) => candidate.key === key)
  return stage ? toStage(stage) : null
}

/**
 * El mismo bug que `resolveStageInSameSet`, del lado de las TAREAS.
 *
 * El selector de estado ofrece las fases aplanadas —las del set del
 * **sistema**—, y el backend sólo acepta fases del set de tareas de la empresa
 * de la tarea, que es un clon con ids propios. Mandar el id del sistema daba
 * 409 "La columna pedida no pertenece al juego de columnas de tareas": el
 * estado cambiaba un instante (optimista) y volvía solo, en toda empresa.
 *
 * Se traduce por `key` al set de la empresa. `null` = ese set no está entre
 * los que llegaron (quien llama decide si refrescar).
 */
export function resolveTaskStageForCompany(
  sets: readonly StageSetRead[],
  companyId: Uuid,
  targetStageId: Uuid,
): Uuid | null {
  const key = sets.flatMap((set) => set.stages).find((stage) => stage.id === targetStageId)?.key
  if (!key) return null

  const companySet = sets.find((set) => set.company_id === companyId)
  return companySet?.stages.find((stage) => stage.key === key)?.id ?? null
}

/** La fase de una fila de listado: el backend manda etiqueta y categoría. */
function stageFromRow(row: {
  stage_id?: Uuid
  stage_label: string
  stage_category: Stage['category']
}): Stage {
  return {
    id: row.stage_id ?? '',
    name: row.stage_label,
    category: row.stage_category,
    position: 0,
    color: null,
  }
}

/* -------------------------------------------------------------- proyectos */

export function toProjectSummary(
  dto: ProjectListItem,
  users: UserIndex = EMPTY_USER_INDEX,
): ProjectSummary {
  return {
    id: dto.id,
    name: dto.name,
    company: companyFromRow(dto),
    stage: stageFromRow(dto),
    is_ongoing: dto.is_ongoing,
    position: dto.position,
    internal_owner: lookupUser(users, dto.internal_owner_id),
    internal_owner_id: dto.internal_owner_id,
    // FALTA en el listado: el contacto del cliente solo viaja en el detalle.
    client_contact: null,
    next_due_date: dto.next_due_date,
    // Y las dos fechas del proyecto, que son los extremos de la barra en la
    // linea de tiempo.
    start_date: dto.start_date,
    target_end_date: dto.target_end_date,
    // El listado SÍ trae la fecha de cierre: es lo que le permite a `/board`
    // recortar "Culminado" a los últimos 30 días.
    actual_end_date: dto.actual_end_date,
    open_tasks: dto.open_tasks,
    overdue_tasks: dto.overdue_tasks,
    is_client_visible: dto.is_client_visible,
    // Y el semáforo, para no tener que abrir cada ficha.
    health: dto.health,
    tag_ids: dto.tag_ids ?? [],
  }
}

/* ---------------------------------------------------------- soporte apps */

/**
 * Sólo http(s): estas URLs llegan de otro servicio y terminan en un `href`. Un
 * `javascript:` ahí sería XSS al hacer clic. El backend ya las rechaza; esto es
 * la segunda capa, por si un dato viejo o un backend mal configurado deja pasar
 * una.
 */
export function urlWebSegura(url: string | null): string | null {
  return url && /^https?:\/\/\S+$/i.test(url) ? url : null
}

/** `TicketItem` → `SupportTicket`. Se resuelve el responsable y se filtran las URLs. */
export function toSupportTicket(
  dto: TicketItem,
  users: UserIndex = EMPTY_USER_INDEX,
): SupportTicket {
  return {
    id: dto.id,
    code: dto.code,
    severity: dto.severity,
    kind: dto.kind,
    app: { slug: dto.tool_slug, name: dto.app_name },
    reporter: { name: dto.reporter_name, email: dto.reporter_email },
    page_url: urlWebSegura(dto.page_url),
    source_url: urlWebSegura(dto.source_url),
    created_at: dto.created_at,
    task_id: dto.task_id,
    project: { id: dto.project_id, name: dto.project_name },
    linkedProject:
      dto.linked_project_id && dto.linked_project_name
        ? { id: dto.linked_project_id, name: dto.linked_project_name }
        : null,
    title: dto.title,
    stage: { label: dto.stage_label, category: dto.stage_category as StageCategory },
    assignee: lookupUser(users, dto.assignee_id),
    assignee_id: dto.assignee_id,
    due_date: dto.due_date,
    completed_at: dto.completed_at,
    resolved_by_ai: dto.resolved_by_ai,
  }
}

/* ----------------------------------------------------------------- tareas */

/**
 * `TaskListItem` **no trae `stage_id`**, solo `stage_label`. El tablero necesita
 * el id para agrupar y para mover, así que se resuelve contra el set de fases de
 * tareas — dentro de un set las etiquetas son únicas.
 */
export function toTask(
  dto: TaskListItem,
  options: { users?: UserIndex; taskStages?: readonly Stage[] } = {},
): Task {
  const { users = EMPTY_USER_INDEX, taskStages = [] } = options
  const matched = taskStages.find((stage) => stage.name === dto.stage_label)

  return {
    id: dto.id,
    title: dto.title,
    // FALTA en el listado: la descripción solo viaja en el detalle.
    description_md: null,
    project: {
      id: dto.project_id,
      name: dto.project_name,
      // FALTA en el listado: si el proyecto es ongoing no viaja con la tarea.
      is_ongoing: false,
      company: companyFromRow(dto),
    },
    assignee: lookupUser(users, dto.assignee_id),
    assignee_id: dto.assignee_id,
    due_date: dto.due_date,
    stage: matched ?? stageFromRow(dto),
    position: dto.position,
    // FALTA en el listado: la estimación solo viaja en el detalle.
    estimate_hours: null,
    completed_at: dto.completed_at,
    // FALTA en el listado: la visibilidad solo viaja en el detalle.
    is_client_visible: false,
    tag_ids: dto.tag_ids ?? [],
  }
}

/**
 * Lo que devuelve una mutación (`TaskRead`) reconciliado sobre lo que ya está
 * en la cache. Se parchea, no se reemplaza: `TaskRead` es más chico que la fila
 * de listado (no trae nombre de proyecto ni de empresa) y pisar la fila entera
 * dejaría la tabla sin la mitad de sus columnas.
 */
/**
 * `defined` no es cosmético: una respuesta parcial (un stub de test, un DTO que
 * el servidor recorta) traía `name: undefined` y el spread PISABA el nombre que
 * ya estaba en la cache. Una fila se quedaba sin título después de un drop.
 */
function defined<T extends object>(value: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(value).filter(([, item]) => item !== undefined),
  ) as Partial<T>
}

export function patchFromTaskRead(dto: TaskRead, stages: readonly Stage[] = []): Partial<Task> {
  const stage = stages.find((candidate) => candidate.id === dto.stage_id)
  return defined({
    id: dto.id,
    title: dto.title,
    description_md: dto.description_md,
    assignee_id: dto.assignee_id,
    due_date: dto.due_date,
    position: dto.position,
    estimate_hours: dto.estimate_hours === null ? null : Number(dto.estimate_hours),
    completed_at: dto.completed_at,
    is_client_visible: dto.is_client_visible,
    tag_ids: dto.tag_ids,
    ...(stage ? { stage } : {}),
  })
}

export function patchFromProjectRead(
  dto: ProjectRead,
  stages: readonly Stage[] = [],
): Partial<ProjectSummary> {
  const stage = stages.find((candidate) => candidate.id === dto.stage_id)
  return defined({
    id: dto.id,
    name: dto.name,
    is_ongoing: dto.is_ongoing,
    position: dto.position,
    internal_owner_id: dto.internal_owner_id,
    actual_end_date: dto.actual_end_date,
    is_client_visible: dto.is_client_visible,
    health: dto.health,
    tag_ids: dto.tag_ids,
    ...(stage ? { stage } : {}),
  })
}

/* -------------------------------------------------------------- accesos -- */

/**
 * 1Password publica sus items como `https://start.1password.com/open/i?i=<id>`.
 * El backend guarda el id y el proveedor por separado, así que la URL se arma
 * acá en vez de pedirle al servidor que guarde un link que ya sabe derivar.
 */
export function vaultItemUrl(
  dto: Pick<AccessRead, 'vault_provider' | 'vault_item_id'>,
): string | null {
  if (!dto.vault_item_id) return null
  if (/^https?:\/\//i.test(dto.vault_item_id) || dto.vault_item_id.startsWith('onepassword://')) {
    return dto.vault_item_id
  }
  // El proveedor lo escribe una persona: puede venir `1Password`, `1password`
  // o `1Password Business`. Comparar exacto dejaba el link sin armar y el botón
  // desaparecía sin que nadie entendiera por qué.
  if (/1\s*password/i.test(dto.vault_provider ?? '')) {
    return `https://start.1password.com/open/i?i=${encodeURIComponent(dto.vault_item_id)}`
  }
  return null
}

export function toCompanyAccess(dto: AccessRead): CompanyAccess {
  return {
    id: dto.id,
    company_id: dto.company_id,
    label: dto.label,
    category: dto.category as CompanyAccess['category'],
    kind: dto.kind,
    url: dto.url,
    username: dto.username,
    vault_provider: dto.vault_provider,
    vault_item_id: dto.vault_item_id,
    vault_item_url: vaultItemUrl(dto),
    notes: dto.notes_internal,
    has_secret: dto.has_secret,
  }
}

/* ------------------------------------------------------------ documentos - */

export function toDocumentSummary(
  dto: DocumentListItem,
  companyId: Uuid,
  users: UserIndex = EMPTY_USER_INDEX,
): DocumentSummary {
  return {
    id: dto.id,
    company_id: companyId,
    project_id: dto.project_id,
    title: dto.title,
    doc_type: dto.doc_type,
    kind: dto.kind,
    external_url: dto.external_url,
    visibility: dto.visibility,
    current_version: dto.current_version,
    updated_at: dto.updated_at,
    // Quién guardó la versión vigente. Sale del autor de la fila de
    // `document_versions` que señala `current_version`, así que un cambio que
    // no versiona (mover de `doc_type`, publicar al portal) no lo mueve.
    updated_by: lookupUser(users, dto.updated_by),
  }
}

export function toDocumentDetail(
  dto: DocumentRead,
  users: UserIndex = EMPTY_USER_INDEX,
): DocumentDetail {
  return {
    id: dto.id,
    company_id: dto.company_id,
    project_id: dto.project_id,
    title: dto.title,
    doc_type: dto.doc_type,
    kind: dto.kind,
    content_md: dto.content_md,
    external_url: dto.external_url,
    visibility: dto.visibility,
    current_version: dto.current_version,
    updated_at: dto.updated_at,
    // `created_by_id` es quien lo CREÓ, no quien guardó la última versión. Es
    // lo más cerca que llega el contrato de hoy.
    updated_by: lookupUser(users, dto.created_by_id),
  }
}

/* ---------------------------------------------------------- arquitectura - */

export function toProjectComponent(dto: ComponentRead, companyId: Uuid = ''): ProjectComponent {
  return {
    id: dto.id,
    company_id: companyId,
    project_id: dto.project_id,
    key: dto.key,
    name: dto.name,
    kind: dto.kind as ProjectComponent['kind'],
    tech: dto.tech,
    version: dto.version,
    hosting: dto.hosting,
    environment: dto.environment,
    url: dto.url,
    repository_id: dto.repository_id,
    access_id: dto.access_id,
    document_id: dto.document_id,
    notes_md: dto.notes_md,
    position_x: dto.position_x,
    position_y: dto.position_y,
    is_client_visible: dto.is_client_visible,
  }
}

export function toComponentLink(dto: ComponentLinkRead): ComponentLink {
  return {
    id: dto.id,
    project_id: dto.project_id,
    from_component_id: dto.from_component_id,
    to_component_id: dto.to_component_id,
    label: dto.label,
    protocol: dto.protocol as ComponentLink['protocol'],
    is_async: dto.is_async,
    notes: dto.notes,
  }
}

/** `sync_error` es jsonb libre: nos quedamos con lo que sabemos leer. */
function toSyncError(raw: Record<string, unknown> | null): ProjectRepository['sync_error'] {
  if (!raw) return null
  const message = typeof raw.message === 'string' ? raw.message : 'La sincronización falló.'
  const at = typeof raw.at === 'string' ? raw.at : null
  return { message, at }
}

export function toProjectRepository(dto: RepositoryRead): ProjectRepository {
  return {
    id: dto.id,
    project_id: dto.project_id,
    provider: dto.provider as ProjectRepository['provider'],
    owner: dto.owner,
    name: dto.name,
    url: dto.url,
    default_branch: dto.default_branch ?? 'main',
    role: dto.role as ProjectRepository['role'],
    is_private: dto.is_private,
    last_commit_sha: dto.last_commit_sha,
    last_commit_at: dto.last_commit_at,
    last_commit_author: dto.last_commit_author,
    open_prs: dto.open_prs,
    primary_language: dto.primary_language,
    synced_at: dto.synced_at,
    // FALTA: el backend no distingue "último intento" de "última que salió
    // bien". Con error, `synced_at` es el intento, así que no hay dato fresco
    // que mostrar y va `null`.
    last_sync_ok_at: dto.sync_error ? null : dto.synced_at,
    sync_error: toSyncError(dto.sync_error),
  }
}

export function toArchitectureTemplate(dto: ArchitectureTemplateRead): ArchitectureTemplate {
  return {
    key: dto.key,
    name: dto.name,
    description: dto.description,
    // El backend lo llama `components` a secas.
    component_count: dto.components,
  }
}

/* -------------------------------------------------------------- actividad */

/**
 * La actividad de un proyecto sale del log de auditoría
 * (`GET /audit/entries?entity_type=project&entity_id=…`). No hay endpoint
 * `/projects/{id}/activity`.
 *
 * **Acá no se arma ninguna frase.** Había un `summary` que pegaba un verbo en
 * español (`'actualizó'`) al `entity_type` crudo del log. Eso era texto de
 * interfaz escrito en la capa de adaptación: invisible para cualquiera que
 * buscara qué traducir, y concatenado, que es lo que produce frases absurdas
 * apenas el idioma cambia el orden de las palabras. Lo que viaja ahora es el
 * `action` crudo, y la pantalla elige una clave entera por acción.
 */
export function toActivityEntry(
  dto: AuditEntryRead,
  users: UserIndex = EMPTY_USER_INDEX,
): ActivityEntry {
  return {
    id: dto.id,
    at: dto.created_at,
    actor: lookupUser(users, dto.actor_id),
    action: dto.action,
    // `before`/`after` venían llegando y se tiraban acá. El diff se calcula en
    // el adaptador —y no al dibujar— para que la pantalla reciba una lista ya
    // limpia de ruido (el `priority_rank` de cada arrastre, sobre todo).
    changes: diffDeAuditoria(dto.before, dto.after),
  }
}
